import {
  DeleteAktivitetRequest,
  EnkelInventeringAktivitetPost,
  EnkelinventeringAktivitet,
  HTTP_STATUS,
  InventeringPost,
  InventeringPreliminar,
  InventeringRequest,
  KulturvardeForstortPost
} from 'models';

import { bebyggelseApiRTK } from 'api/bebyggelseApiRTK';

const AKTIVITET_URL = `/aktivitet`;
const INVENTERING_URL = `${AKTIVITET_URL}/inventering`;
const ENKELINVENTERING_URL = `${AKTIVITET_URL}/enkelinventering`;

export type ExtendedInventeringPreliminar = InventeringPreliminar & { http_status_code: HTTP_STATUS };
export type ExtendedEnkelInventeringAktivitet = EnkelinventeringAktivitet & {
  http_status_code: HTTP_STATUS;
  eTag: string;
};

export const aktivitetPreliminarSlice = bebyggelseApiRTK.injectEndpoints({
  endpoints: (build) => ({
    getAktivitetPreliminar: build.query<ExtendedInventeringPreliminar, string>({
      transformErrorResponse(baseQueryReturnValue, _fetchBaseQueryMeta, id) {
        return {
          ignoreHttpError: [HTTP_STATUS.NOT_FOUND],
          ...baseQueryReturnValue,
          args: { id }
        };
      },
      transformResponse: (response: any, meta) => {
        return {
          http_status_code: meta?.response?.status,
          eTag: meta?.response?.headers?.get('etag'),
          ...response
        };
      },
      query: (id) => ({
        url: `${AKTIVITET_URL}/${id}/preliminar`,
        method: 'GET'
      }),
      providesTags: (result) => [{ type: 'AktivitetPreliminar', id: result?.aktivitet_id }]
    }),
    postAktivitetKulturvardeForstort: build.mutation<
      ExtendedEnkelInventeringAktivitet,
      { bebyggelseobjektId: string; kulturvardeForstortPost: KulturvardeForstortPost }
    >({
      transformResponse: (response: any, meta) => {
        return {
          http_status_code: meta?.response?.status,
          eTag: meta?.response?.headers?.get('etag'),
          ...response
        };
      },
      query: ({ bebyggelseobjektId, kulturvardeForstortPost }) => ({
        url: `${AKTIVITET_URL}/${bebyggelseobjektId}/kulturvardeforstort`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(kulturvardeForstortPost)
      })
    }),
    postAktivitetEnkelInventering: build.mutation<ExtendedEnkelInventeringAktivitet, EnkelInventeringAktivitetPost>({
      transformResponse: (response: any, meta) => {
        return {
          http_status_code: meta?.response?.status,
          eTag: meta?.response?.headers?.get('etag'),
          ...response
        };
      },
      query: (enkelinventeringPost) => ({
        url: `${ENKELINVENTERING_URL}`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(enkelinventeringPost)
      })
    }),
    postAktivitetInventeringPreliminar: build.mutation<ExtendedInventeringPreliminar, InventeringPost>({
      transformErrorResponse(baseQueryReturnValue) {
        return {
          ignoreHttpError: [HTTP_STATUS.BAD_REQUEST],
          ...baseQueryReturnValue
        };
      },
      transformResponse: (response: any, meta) => {
        return {
          http_status_code: meta?.response?.status,
          eTag: meta?.response?.headers?.get('etag'),
          ...response
        };
      },
      query: (inventeringPost) => ({
        url: `${INVENTERING_URL}`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inventeringPost)
      })
    }),
    putAktivitetInventeringPreliminar: build.mutation<ExtendedInventeringPreliminar, InventeringRequest>({
      transformErrorResponse(baseQueryReturnValue) {
        return {
          ignoreHttpError: [HTTP_STATUS.BAD_REQUEST],
          ...baseQueryReturnValue
        };
      },
      transformResponse: (response: any, meta) => {
        return {
          http_status_code: meta?.response?.status,
          eTag: meta?.response?.headers?.get('etag'),
          ...response
        };
      },
      query: (inventeringRequest) => ({
        url: `${INVENTERING_URL}/${inventeringRequest.aktivitetId}/preliminar`,
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'If-Match': inventeringRequest.eTag,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inventeringRequest.inventering)
      }),
      async onQueryStarted({ aktivitetId }, { dispatch, queryFulfilled }) {
        const { data: updatedInventeringPreliminar } = await queryFulfilled;
        dispatch(
          aktivitetPreliminarSlice.util.updateQueryData('getAktivitetPreliminar', aktivitetId, (draft) => {
            Object.assign(draft, updatedInventeringPreliminar);
          })
        );
      }
    }),
    deleteAktivitet: build.mutation<void, DeleteAktivitetRequest>({
      transformResponse: (response: any, meta) => {
        return {
          http_status_code: meta?.response?.status,
          method: meta?.request.method,
          eTag: meta?.response?.headers?.get('etag'),
          ...response
        };
      },
      query: (deleteAktivitetRequest) => {
        return {
          url: `${AKTIVITET_URL}/${deleteAktivitetRequest.aktivitetId}`,
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            'If-Match': deleteAktivitetRequest.eTag,
            'Content-Type': 'application/json'
          }
        };
      }
    }),
    deleteAktivitetPreliminar: build.mutation<void, DeleteAktivitetRequest>({
      transformResponse: (response: any, meta) => {
        return {
          http_status_code: meta?.response?.status,
          method: meta?.request.method,
          eTag: meta?.response?.headers?.get('etag'),
          ...response
        };
      },
      query: (deleteAktivitetRequest) => ({
        url: `${AKTIVITET_URL}/${deleteAktivitetRequest.aktivitetId}/preliminar`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'If-Match': deleteAktivitetRequest.eTag,
          'Content-Type': 'application/json'
        }
      }),
      invalidatesTags: (_result, _error, deleteAktivitetRequest) => [
        { type: 'AktivitetPreliminar', id: deleteAktivitetRequest.aktivitetId }
      ]
    }),
    patchPubliceraAktivitetPreliminar: build.mutation<
      ExtendedInventeringPreliminar | { http_status_code: HTTP_STATUS },
      string
    >({
      transformResponse: (response: any, meta) => {
        return {
          http_status_code: meta?.response?.status,
          method: meta?.request.method,
          ...response
        };
      },
      query: (aktivitetId) => ({
        url: `${AKTIVITET_URL}/${aktivitetId}/preliminar/publicera`,
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      }),
      async onQueryStarted(aktivitetId, { dispatch, queryFulfilled }) {
        const { data: patchedAcceptedInventeringPreliminar } = await queryFulfilled;
        if (patchedAcceptedInventeringPreliminar?.http_status_code === HTTP_STATUS.ACCEPTED) {
          dispatch(
            aktivitetPreliminarSlice.util.updateQueryData('getAktivitetPreliminar', aktivitetId, (draft) => {
              Object.assign(draft, patchedAcceptedInventeringPreliminar);
            })
          );
        }
      },
      invalidatesTags: (result, _error, aktivitetId) =>
        result?.http_status_code === HTTP_STATUS.CREATED ? [{ type: 'AktivitetPreliminar', id: aktivitetId }] : []
    })
  })
});

export const {
  useGetAktivitetPreliminarQuery,
  usePutAktivitetInventeringPreliminarMutation,
  usePostAktivitetInventeringPreliminarMutation,
  usePostAktivitetEnkelInventeringMutation,
  usePostAktivitetKulturvardeForstortMutation,
  useDeleteAktivitetPreliminarMutation,
  useDeleteAktivitetMutation,
  usePatchPubliceraAktivitetPreliminarMutation
} = aktivitetPreliminarSlice;
